import { createContext } from 'react';
import type { UserDto } from '../api';
import type { StoredTokens } from '../auth/tokenStorage';

// rate_limited is not a separate step — it's an attribute of mfa_challenge / mfa_enrollment.
// Both steps carry an optional rateLimitUntil (epoch ms). The banner renders inline on
// those pages, which matches the described UX (no separate route for rate-limiting).

export type MfaChallengeState = {
  step: 'mfa_challenge';
  challengeToken: string;
  rateLimitUntil?: number;
};

export type MfaEnrollmentState = {
  step: 'mfa_enrollment';
  challengeToken: string;
  qrCodeUri: string;
  manualEntryKey: string;
  rateLimitUntil?: number;
};

export type LoginFlowState =
  | { step: 'idle' }
  | { step: 'submitting_credentials' }
  | MfaChallengeState
  | MfaEnrollmentState
  // pendingTokens/pendingUser held here until the user confirms recovery codes.
  // authenticate() is called AFTER the user checks the box and hits Continue,
  // so that LoginPage's redirect guard cannot fire before codes are shown.
  | { step: 'recovery_codes_shown'; recoveryCodes: string[]; pendingTokens: StoredTokens; pendingUser: UserDto | null }
  | { step: 'authenticated' }
  | { step: 'error'; message: string };

export type LoginFlowAction =
  | { type: 'SUBMIT_CREDENTIALS' }
  | { type: 'MFA_CHALLENGE'; challengeToken: string }
  | { type: 'MFA_ENROLLMENT'; challengeToken: string; qrCodeUri: string; manualEntryKey: string }
  | { type: 'RECOVERY_CODES'; recoveryCodes: string[]; pendingTokens: StoredTokens; pendingUser: UserDto | null }
  | { type: 'AUTHENTICATED' }
  | { type: 'ERROR'; message: string }
  | { type: 'RATE_LIMITED'; retryAfterSeconds: number }
  | { type: 'CLEAR_RATE_LIMIT' }
  | { type: 'RESET' };

export interface LoginFlowContextValue {
  state: LoginFlowState;
  dispatch: React.Dispatch<LoginFlowAction>;
}

export const LoginFlowContext = createContext<LoginFlowContextValue | null>(null);
